import { Box, Typography } from "@mui/material";

const ScopeOfService = (
  <Typography variant="body1" textAlign={"justify"} marginLeft={3} paragraph>
    Under this Agreement, the Vendor is permitted to offer one or more services
    on the Kozyclan platform. The services may range from one-off experiences to
    ongoing, long-term offerings. The Vendor is responsible for providing
    accurate, detailed, and up-to-date descriptions of each service, including
    features, limitations, and any necessary conditions. It is expected that the
    Vendor maintains the highest quality in service delivery, ensuring that all
    services conform to the descriptions provided and meet industry standards.
    The Vendor is also obligated to update service details regularly to reflect
    any changes in availability or quality, thus ensuring that consumers can
    make informed decisions when booking.
  </Typography>
);

const PaymentFirstParagraph = (
  <Typography
    variant="body1"
    textAlign={"justify"}
    gutterBottom
    paragraph
    marginLeft={3}
  >
    All payments for services offered through the Kozyclan platform are subject
    to a clear and structured process. At the time a user makes a booking, full
    payment is collected by the Company. Out of the total amount paid by the
    user, a commission of 10% is retained by the Company as a fee for
    facilitating the transaction and marketing the service. The remaining funds
    are disbursed to the Vendor in two installments: the first installment,
    constituting 50% of the service fee, is credited to the Vendor's designated
    bank account on the day following the commencement of the service; the final
    installment, comprising the remaining 50%, is disbursed within 07 days after
    the service is completed. In addition, the Vendor is responsible for
    handling all applicable taxes (including GST) in accordance with statutory
    requirements. Should there be any reduction in service quality or failure to
    deliver the service as promised, any required refunds or adjustments will be
    deducted from the Vendor's payments in line with the processes set out in
    this Agreement.
  </Typography>
);

const PaymentFirstParagraphInternational = (
  <Typography
    variant="body1"
    textAlign={"justify"}
    gutterBottom
    paragraph
    marginLeft={3}
  >
    All payments for services offered through the Kozyclan platform are subject
    to a clear and structured process. At the time a user makes a booking, full
    payment is collected by the Company. Out of the total amount paid by the
    user, a commission of 10% is retained by the Company for domestic trips, 8%
    for short-haul international trips (all except Europe) and 6% for long-haul
    international trips (Europe) as a fee for facilitating the transaction and
    marketing the service. The remaining funds are disbursed to the Vendor seven
    days before the commencement of the service. In addition, the Vendor is
    responsible for handling all applicable taxes (including GST) in accordance
    with statutory requirements. Should there be any reduction in service
    quality or failure to deliver the service as promised, any required refunds
    or adjustments will be deducted from the Vendor's payments in line with the
    processes set out in this Agreement.
  </Typography>
);

const PaymentTermsRemainder = (
  <>
    <Typography
      variant="body1"
      textAlign={"justify"}
      gutterBottom
      paragraph
      marginLeft={3}
    >
      All transactions on the Kozyclan Platform are processed via secure
      third-party payment gateways. The Company reserves the right to charge a
      convenience fee to buyers at its discretion, which may be waived under
      specific circumstances. While the Platform ensures secure payment
      processing and facilitates service delivery, the Vendor remains solely
      responsible for fulfilling the service as described. Refunds or
      adjustments due to service deficiencies will be processed in accordance
      with Clause 3 and deducted from the Vendor's payments.
    </Typography>
    <ul
      style={{
        listStyle: "disc",
        marginLeft: "30px",
        marginBottom: "10px",
        gap: "10px",
      }}
    >
      <li style={{ marginBottom: "10px" }}>
        <strong>GST on Trip Charges:</strong> A 5% GST will be levied on trip
        charges as per prevailing government regulations. Any change in
        applicable rates will result in a corresponding adjustment in GST
        charged.
      </li>
      <li style={{ marginBottom: "10px" }}>
        <strong>GST on Platform Commission:</strong> Kozyclan charges agreed
        commission on transactions, subject to 18% GST as per government
        guidelines. Any revision in GST rates will be applied accordingly.
      </li>
      <li>
        <strong>GST Compliance:</strong> All GST-related transactions will be
        duly reported through the official GST portal, and the applicable GST
        amounts will be available for input tax credit as per government norms.
      </li>
    </ul>
  </>
);

const PayementTerm = (
  <>
    {PaymentFirstParagraph}
    {PaymentTermsRemainder}
  </>
);

const PayementTermInternational = (
  <>
    {PaymentFirstParagraphInternational}
    {PaymentTermsRemainder}
  </>
);

const Pricing = (
  <Box marginLeft={3}>
    <Typography variant="body1" textAlign={"justify"} paragraph>
      The Vendor must adhere to the pricing guidelines established by the
      Kozyclan platform (the "Platform"). The price displayed to users on the
      Platform must be consistent with the price offered on the Vendor's own
      website or any other sales channel, and no additional charges or inflated
      prices may be applied. In the event of any discrepancy, the Vendor will
      receive a warning, and repeated violations (more than three instances
      within a calendar year) may lead to suspension or termination of the
      Vendor's account.
    </Typography>
    <Typography variant="body1" textAlign={"justify"} paragraph>
      Regarding cancellations, the Vendor agrees that where a booking is
      cancelled and the same user rebooks a comparable service, the subsequent
      booking will be treated as a replacement for the cancelled service rather
      than as a new transaction, thereby preserving the overall booking count.
      No cancellation fee shall accrue to the Vendor in respect of a
      cancellation for which a replacement service is provided.
    </Typography>
    <Typography variant="body1" textAlign={"justify"} paragraph>
      For cancellations where a replacement service is not provided, the
      cancellation fee payable to the Vendor is determined by the trip category
      and the number of days between the date of cancellation and the trip start
      date (the "cancellation window"), as set out in Section 3. In each case,
      the stated cancellation fee is paid to the Vendor as full payment after
      deduction of the agreed commissions.
    </Typography>
    <Typography variant="body1" fontWeight={600} paragraph>
      Cancellation Fee Schedules by Trip Category
    </Typography>
    <Typography variant="body1" textAlign={"justify"} paragraph>
      For the purposes of this Section, "days prior" means calendar days counted
      from the date of cancellation up to (but not including) the trip start or
      check-in date. Where a trip could fall within more than one category, the
      international categories in clauses 3.3 and 3.4 take precedence: any
      international trip is governed by the applicable international schedule
      regardless of its duration, and the duration-based schedules in clauses
      3.1 and 3.2 apply only to domestic trips.
    </Typography>
    <Typography variant="body1" fontWeight={600} paragraph>
      3.1 Weekend Trips (domestic trips of 3 nights or fewer)
    </Typography>
    <ul style={{ listStyle: "disc", marginLeft: "30px", marginBottom: "10px" }}>
      <li>
        Cancellations made 0-5 days prior: 100% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 6-10 days prior: 50% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 11-15 days prior: 25% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 16-30 days prior: 10% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>Cancellations made more than 30 days prior: no cancellation fee.</li>
    </ul>
    <Typography variant="body1" fontWeight={600} paragraph>
      3.2 Domestic Trips (domestic trips of more than 3 nights)
    </Typography>
    <ul style={{ listStyle: "disc", marginLeft: "30px", marginBottom: "10px" }}>
      <li>
        Cancellations made 0-7 days prior: 100% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 8-15 days prior: 50% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 16-21 days prior: 25% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 22-30 days prior: 10% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>Cancellations made more than 30 days prior: no cancellation fee.</li>
    </ul>
    <Typography variant="body1" fontWeight={600} paragraph>
      3.3 Short Haul International Trips (all international destinations except
      Europe)
    </Typography>
    <ul style={{ listStyle: "disc", marginLeft: "30px", marginBottom: "10px" }}>
      <li>
        Cancellations made 0-15 days prior: 100% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 16-30 days prior: 75% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 31-45 days prior: 50% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 46-60 days prior: 25% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>Cancellations made more than 60 days prior: no cancellation fee.</li>
    </ul>
    <Typography variant="body1" fontWeight={600} paragraph>
      3.4 Long Haul International Trips (Europe)
    </Typography>
    <ul style={{ listStyle: "disc", marginLeft: "30px", marginBottom: "10px" }}>
      <li>
        Cancellations made 0-30 days prior: 100% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 31-45 days prior: 75% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 46-60 days prior: 50% cancellation fee (full payment
        to the Vendor after deducting agreed commissions).
      </li>
      <li>
        Cancellations made 61 days or more prior: 25% cancellation fee (full
        payment to the Vendor after deducting agreed commissions).
      </li>
    </ul>
    <Typography variant="body1" textAlign={"justify"} paragraph>
      These measures are designed to ensure fairness and clarity for both the
      Vendor and the consumers.
    </Typography>
    <Typography variant="body1" fontWeight={600} paragraph>
      Changes Post-Booking
    </Typography>
    <ul style={{ listStyle: "disc", marginLeft: "30px" }}>
      <li style={{ marginBottom: "10px", textAlign: "justify" }}>
        Any modifications to service offerings, pricing, or plans made by the
        Vendor after a booking has been confirmed by the user shall not affect
        existing bookings. The Vendor is obligated to honour the terms, pricing,
        and service descriptions as they existed at the time of the booking.
      </li>
      <li style={{ marginBottom: "10px", textAlign: "justify" }}>
        If the Vendor unilaterally alters or cancels a service in a manner that
        directly impacts a confirmed booking (for example, discontinuing a
        service, changing dates or locations, or materially altering the
        offering), the Vendor will not be entitled to any cancellation charges
        for such cancellations. However, the Vendor must promptly notify the
        Company and the affected users of such changes. The Company reserves the
        right to impose penalties or suspend the Vendor's account if such
        changes are deemed unreasonable, frequent, or indicative of poor
        operational practices.
      </li>
    </ul>
  </Box>
);

const Prohibited = (
  <>
    <Typography
      variant="body1"
      textAlign={"justify"}
      gutterBottom
      marginLeft={3}
      paragraph
    >
      The Vendor is expected to conduct its business in a manner that preserves
      the integrity and reputation of the Kozyclan platform. This includes a
      strict prohibition against any form of abuse, misconduct, or sexual
      harassment by the Vendor or its employees, agents, or representatives. The
      Vendor is required to brief all team members on these standards and ensure
      full compliance. Any confirmed instances of such prohibited conduct will
      result in immediate suspension or termination of the Vendor's account.
      Additionally, the Vendor is expressly forbidden from attempting to
      circumvent the Kozyclan platform by contacting users directly to alter,
      cancel, or move bookings to an independent platform. Should the Vendor
      engage in such activities, it will not only face suspension or termination
      but may also be liable for damages. Furthermore, if the Vendor fails to
      deliver the service as advertised, the Vendor shall incur a penalty equal
      to 100% of the service fee plus an additional 50% of the service fee,
      subject to the Company's verification and mutual agreement between the
      Parties.
    </Typography>
    <Typography variant="body1" textAlign={"justify"} marginLeft={3} paragraph>
      Vendors are strictly prohibited from including or sharing any personal or
      business communication channels—such as email addresses, phone numbers,
      social media handles, or any other direct contact information—in vendor
      listings, promotional materials, photos, or text. Any attempt to share
      such information will result in a formal warning. Should a vendor receive
      three warnings within a 12-month period, their agreement will be
      immediately terminated, and they will be permanently banned from our
      platform.
    </Typography>
  </>
);

const PartiesRights = (
  <ul style={{ listStyle: "lower-alpha", marginLeft: "20px", padding: "10px" }}>
    <li style={{ marginBottom: "10px" }}>
      <strong>Rights & Obligations of the Company:</strong>
      <br />
      The Vendor represents, warrants and covenants that:
      <ul
        style={{
          listStyle: "lower-alpha",
          marginLeft: "30px",
          marginTop: "10px",
        }}
      >
        <li style={{ marginBottom: "10px", textAlign: "justify" }}>
          Kozyclan reserves the right to operate, maintain, and continuously
          improve the digital marketplace platform, ensuring it is secure,
          reliable, and accessible to both vendors and users. As the platform
          operator, Kozyclan holds the right to set guidelines and policies that
          govern service listings, pricing, payment processing, and content
          usage. This includes the authority to review and monitor the quality
          of vendor-provided services and the accuracy of the information
          published. Kozyclan also has the right to enforce compliance by
          issuing warnings, imposing penalties, or suspending or terminating a
          vendor's account if any terms of the Agreement are violated.
          Additionally, the Company is entitled to use any content provided by
          the Vendor such as images, descriptions, or promotional materials for
          marketing, advertising, or platform promotional purposes, as permitted
          by the Agreement.
        </li>
        <li style={{ marginBottom: "10px", textAlign: "justify" }}>
          In return for the rights granted under the Agreement, Kozyclan is
          obligated to provide a robust, secure, and user-friendly platform that
          facilitates the listing, booking, and payment processing of travel and
          hospitality services. The Company must ensure that user payments are
          collected at the time of booking and disbursed to the Vendor in a
          timely manner according to the agreed-upon payment schedule and
          commission structure. Kozyclan is also responsible for maintaining the
          operational integrity of the platform, which includes offering
          technical support, addressing vendor or user issues promptly, and
          communicating any updates or changes in policies effectively.
          Furthermore, the Company is bound to comply with all relevant
          regulatory and statutory requirements, including those related to
          digital payments, data protection, and consumer rights, thereby
          ensuring that the marketplace operates transparently and in good faith
          for all parties involved.
        </li>
        <li style={{ marginBottom: "10px", textAlign: "justify" }}>
          The Vendor acknowledges that Kozyclan will make reasonable efforts to
          support users in resolving disputes or service deficiencies. However,
          the Vendor bears ultimate responsibility for fulfilling the service as
          advertised. The Platform does not guarantee user satisfaction in cases
          where the Vendor has materially complied with service descriptions but
          minor deviations occur due to the inherent variability of
          community-driven experiences.
        </li>
      </ul>
    </li>
    <li style={{ marginTop: "20px" }}>
      <strong style={{ paddingBottom: "20px" }}>
        Rights And Obligations of the Vendor:
      </strong>
      <br />
      The Company is required to fulfil the following obligations:
      <ul
        style={{
          listStyle: "lower-alpha",
          marginLeft: "30px",
          marginTop: "20px",
        }}
      >
        <li style={{ marginTop: "20px", textAlign: "justify" }}>
          The Vendor is entitled to leverage the Kozyclan platform to list,
          promote, and sell its travel and hospitality services. By
          participating in the marketplace, the Vendor gains access to a broad
          customer base and the digital infrastructure provided by Kozyclan.
          This includes the right to update, modify, or remove service listings
          in accordance with platform guidelines. Additionally, the Vendor has
          the right to receive payments for services rendered on the platform,
          with funds disbursed as per the pre-established payment schedule after
          deduction of the applicable commission and taxes. The Vendor also
          enjoys the benefit of receiving timely notifications regarding any
          policy changes, updates to the platform, or other relevant information
          that could impact its business operations.
        </li>
        <li style={{ marginTop: "20px", textAlign: "justify" }}>
          To maintain a high standard of service and ensure a seamless customer
          experience, the Vendor is required to provide accurate, complete, and
          up-to-date descriptions of its services. This obligation extends to
          maintaining quality in the delivery of services, ensuring that what is
          promised on the platform matches the actual experience provided to
          users. The Vendor must adhere to all pricing guidelines set by
          Kozyclan, which means the price charged on the platform must be
          consistent with prices on other sales channels, preventing any
          practice of inflating costs or bypassing the agreed-upon pricing
          structure. The Vendor is also required to ensure that any content
          (including images, descriptions, and promotional material) uploaded to
          the platform does not infringe on any third-party intellectual
          property rights, and to secure any necessary licenses or permissions
          for such content.
        </li>
      </ul>
    </li>
  </ul>
);

const Warranties = (
  <Typography
    variant="body1"
    textAlign={"justify"}
    gutterBottom
    paragraph
    marginLeft={3}
  >
    The Vendor represents and warrants that it is a legally established entity
    with full power and authority to enter into and perform under this
    Agreement. The Vendor further warrants that all services provided will
    comply with applicable laws, industry standards, and the representations
    made in any marketing or service descriptions. The Vendor also guarantees
    that any content submitted to the Kozyclan platform is original or properly
    licensed and free of any third-party claims. In the event of any breach of
    these warranties or if any claims arise out of the Vendor's negligence or
    wrongful acts, the Vendor agrees to indemnify, defend, and hold harmless the
    Company, its affiliates, directors, officers, employees, and agents from any
    losses, liabilities, claims, or expenses, including legal fees and costs.
  </Typography>
);

const Liability = (
  <ul style={{ listStyle: "lower-alpha", marginLeft: "30px" }}>
    <li style={{ marginBottom: "20px", textAlign: "justify" }}>
      <strong>Indemnification: </strong>
      <br />
      This clause ensures that Vendor takes responsibility for any claims,
      damages, or lawsuits arising from their services. For example, if a
      customer files a complaint about the quality of training or service,
      Vendor would bear the legal responsibility.
    </li>
    <li style={{ marginBottom: "20px", textAlign: "justify" }}>
      <strong>Limitation of Liability: </strong>
      <br />
      Neither Party shall be liable for any indirect, incidental, consequential,
      or punitive damages arising from or related to this Agreement. In any
      event, the total cumulative liability of either Party for any claims or
      losses arising out of this Agreement shall not exceed the aggregate amount
      of fees received by the Company from the Vendor during the twelve (12)
      months preceding the event that gave rise to the claim, except in cases
      involving breaches related to termination for cause or indemnification
      obligations. This limitation is intended to ensure that the Parties
      maintain a fair balance of risk in the performance of this Agreement. The
      Company shall not be liable for any dissatisfaction arising from minor
      deviations in service quality, provided the Vendor has made reasonable
      efforts to adhere to the advertised service standards. This exclusion
      applies particularly to community-based services where individual
      expectations may vary.
    </li>
  </ul>
);

const ForceMajeure = (
  <Typography
    variant="body1"
    textAlign={"justify"}
    gutterBottom
    paragraph
    marginLeft={3}
  >
    Neither Party shall be responsible for any failure to perform, or delay in
    performing any of its obligations under this Agreement, where and to the
    extent that such a failure or delay results from causes outside the control
    of such Party. Such causes shall include, without limitation, delays caused
    by the other party, acts of God or the public enemy, acts of the government
    in its sovereign or contractual capacity, fires, floods, epidemics,
    quarantine restrictions, freight embargoes, strikes, civil commotion, or the
    like. Ensures neither party is held liable for delays or failures caused by
    uncontrollable events such as natural disasters, pandemics, or strikes.
    Requires prompt notification and reasonable mitigation efforts.
  </Typography>
);
const Indemnity = (
  <Box marginLeft={3}>
    <Typography variant="body1" marginBottom={2} gutterBottom paragraph>
      The Vendor agrees to indemnify and hold harmless The Company, its
      affiliates, and employees from any claims, damages, liabilities, or
      expenses arising from:
    </Typography>
    <ul style={{ listStyle: "disc", marginLeft: "30px" }}>
      <li style={{ marginBottom: "10px" }}>
        Breach of the Vendor's obligations under this Agreement.
      </li>
      <li style={{ marginBottom: "10px" }}>
        Negligence or misconduct by the Vendor or its representatives.
      </li>
      <li style={{ marginBottom: "10px" }}>
        Service quality issues or customer complaints.
      </li>
      <li style={{ marginBottom: "10px" }}>
        Noncompliance with legal regulations.
      </li>
      <li style={{ marginBottom: "10px" }}>
        Actions that harm Kozyclan reputation or finances
      </li>
    </ul>
  </Box>
);

const IPR = (
  <Typography
    variant="body1"
    textAlign={"justify"}
    gutterBottom
    paragraph
    marginLeft={3}
  >
    Unless the Company has otherwise agreed to in writing, nothing herein gives
    the Vendor a right to use any of the Company's trade names, trademarks,
    service marks, logos, domain names, and other distinctive brand features,
    save according to the provisions of this Agreement. Further, the Vendor
    agrees to use any trademark, service mark, trade name or logo of the Company
    authorized by the Company in a manner that does not cause, is likely to
    cause or intends to confuse the owner of such marks, names or logos. Any
    co-branded or jointly created marketing materials during this partnership
    shall remain the sole intellectual property of The Company unless otherwise
    agreed in writing. All rights, titles, and interests in the Company
    platform, including technology, algorithms, and customer data, remain the
    exclusive property of The Company. This clause clarifies that the
    intellectual property (IP) of the services, including proprietary materials
    (logos, designs, training content, etc.), remains the sole property of
    Vendor. The ownership of any IP related to the services they provide is
    maintained by them. Vendor grants Company a non-exclusive, non-transferable,
    royalty-free license to use their IP for the purpose of listing, promoting,
    and marketing the services on the platform. This includes the right to
    display their brand, service descriptions, and materials on the Company
    platform.
  </Typography>
);
const Confidentiality = (
  <Box marginLeft={3}>
    <Typography
      variant="body1"
      textAlign={"justify"}
      marginBottom={2}
      gutterBottom
      paragraph
    >
      Introduced stronger confidentiality obligations regarding customer and
      proprietary data. Extended confidentiality obligations to 5 years after
      termination of the agreement. The Parties may share some information that
      is confidential and proprietary in nature with the other Party to enable
      the parties to interact and work productively. Any such information shall
      be used only for the intended purpose and shall not be disclosed,
      published or otherwise cause to be disclosed to any third party.
    </Typography>
    <Typography
      variant="body1"
      textAlign={"justify"}
      marginBottom={2}
      gutterBottom
      paragraph
    >
      During the term of this Agreement and after the conclusion thereof, the
      Vendor agrees to keep in the strictest confidence and not disclose to any
      third party the confidential information shared, including but not limited
      to, the terms agreed by the Parties herein and the terms and conditions
      set out in this Agreement unless such disclosure is required by law.
    </Typography>
    <Typography
      variant="body1"
      textAlign={"justify"}
      marginBottom={2}
      gutterBottom
      paragraph
    >
      The Vendor agrees to treat any data including but not limited to personal
      data, whether written, oral or visual, disclosed to it or which comes into
      its possession or knowledge in connection with this Agreement through any
      medium as confidential and shall not disclose the same to any others,
      except as may be required by law or as may be required to be disclosed on
      a "need-to-know" basis for implementing this Agreement. This obligation
      shall survive termination of this Agreement.
    </Typography>
  </Box>
);
const TermCondition = (
  <ul style={{ listStyle: "lower-alpha", marginLeft: "30px" }}>
    <li style={{ marginBottom: "10px" }}>
      <strong>Term:</strong> This Agreement is effective from the date of
      acceptance and shall remain in force until terminated by either Party in
      accordance with the terms herein.
    </li>
    <li style={{ marginBottom: "10px" }}>
      <strong>Termination by Either Party</strong>
      This clause provides flexibility for either party to end the Agreement
      with 30 days' written notice. If either party feels that the collaboration
      is no longer mutually beneficial or has encountered operational
      challenges, this clause allows them to terminate the partnership without
      penalties, subject to the notice period.
    </li>
    <li style={{ marginBottom: "10px" }}>
      <strong>Termination for Cause:</strong>
      This clause allows for immediate termination if either party breaches any
      material terms of the Agreement (such as failing to pay commissions,
      violating intellectual property rights, or not fulfilling service
      commitments). The breaching party will have 15 days to remedy the breach,
      or else the non-breaching party can terminate the agreement.
    </li>
    <li style={{ marginBottom: "10px" }}>
      <strong>Termination by notice:</strong> This Agreement may be terminated
      by either party upon giving at least 30 days prior notice in writing to
      the other party.
    </li>
    <li style={{ marginBottom: "10px" }}>
      <strong>Suspensions:</strong> In cases of serious violations (like fraud
      or failure to meet service standards), Company can suspend the listings of
      Vendor on the platform. This prevents unprofessional or unauthorized
      services from affecting the reputation of the platform.
    </li>
  </ul>
);
const Miscellaneous = (
  <ul style={{ listStyle: "lower-alpha", marginLeft: "30px" }}>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Notices and Communication:</strong> All notices and communication
      required shall be in writing and shall be effective on the date of mailing
      to the Parties sent via electronic mail or any mutually agreed upon
      international courier agency, addressed to the designated representatives
      of the Parties. All notices shall be sent to the respective addresses of
      the Parties as stated above or to updated addresses communicated in
      writing.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Waiver:</strong>
      The waiver of a breach of this Agreement or the failure of a Party to
      exercise any right under this Agreement shall in no event constitute a
      waiver as to any other breach, whether similar or dissimilar or prevent
      the exercise of any right under this Agreement.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Severability:</strong>
      Each provision of this Agreement shall be interpreted in such a manner as
      to be effective and valid under applicable law. The invalidity or
      unenforceability of any provision of this Agreement shall in no way affect
      the validity or enforceability of any other provision hereof. Any invalid
      or unenforceable provision shall be deemed severed from this Agreement and
      the balance of this Agreement shall be construed and enforced as if this
      Agreement did not contain the particular provisions(s) held to be invalid
      or unenforceable.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Assignment:</strong> TNeither Party shall assign any rights,
      duties or obligations under this Agreement to any other party, without the
      written prior permission of the respective Party. Emphasized that
      modifications to the agreement require mutual written consent.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Termination and Suspension:</strong> Clarified Kozyclan rights to
      suspend or terminate the vendor's access for noncompliance or poor
      performance.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Headings:</strong> Section headings in this Agreement are merely
      for the convenience of the Parties and should not be construed as having
      any special meaning or importance.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Governing Law:</strong> This Agreement shall be governed by and
      construed in accordance with the laws of India. For purposes of
      enforcement of arbitration awards, equitable relief, or if for any other
      reason litigation is permissible under this agreement, each party hereby
      irrevocably agrees to the personal jurisdiction and venue of the courts at{" "}
      <strong>Gorakhpur, Uttar Pradesh, India</strong>.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Entire Agreement:</strong> This Agreement supersedes all prior
      oral or written representations, communications, or agreements between the
      Parties, and, together with the Schedules hereto, constitutes the final
      and entire understanding of the Parties regarding the subject matter of
      this Agreement. Neither Party has relied on any such prior oral or written
      representations, communications, or agreements.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Dispute Resolution:</strong> In the event of any dispute arising
      out of or in connection with this agreement, including any disputes
      arising out of this Agreement shall be resolved through amicable
      discussions. If disputes remain unresolved, they shall be referred to
      arbitration under the Arbitration and Conciliation Act, 1996. The
      arbitration proceedings shall take place in{" "}
      <strong> Gorakhpur, Uttar Pradesh, India</strong>, and the language of
      arbitration shall be English.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Amendments: </strong> This Agreement may be amended/modified with
      the mutual consent of both Parties, by executing a fresh agreement
      containing such amendments/modifications, or by one Party communicating
      the need for an amendment/modification in writing, and the other Party
      accepting the said proposal and communicating its consent to the same in
      writing.
    </li>
    <li style={{ marginBottom: "10px", textAlign: "justify" }}>
      <strong>Execution: </strong> This Agreement may be executed (including by
      facsimile or internet-based service) by one or more of the Parties on any
      number of separate counterparts. All of such counterparts taken together
      shall be deemed to constitute the same instrument, and (if by facsimile)
      each such facsimile shall have the same force and effect as if an
      original.
    </li>
  </ul>
);

// Vendors listed here see the international commission structure in the
// "Payment and Payment Terms" clause. The "Pricing Consistency and
// Cancellation Policies" clause is now the same for all vendors.
const INTERNATIONAL_VENDOR_IDS = [
  "1687bc42-97f5-440a-acf3-141a57671fe6",
  "8583e1bd-567a-4e6b-b708-a68a957e828d",
];

const getClauses = (userId?: string) => {
  const isInternationalVendor =
    !!userId && INTERNATIONAL_VENDOR_IDS.includes(userId);

  return [
    { id: 1, title: "Scope of Service", data: ScopeOfService },
    {
      id: 2,
      title: "PAYMENT AND PAYMENT TERMS:",
      data: isInternationalVendor ? PayementTermInternational : PayementTerm,
    },
    {
      id: 3,
      title: "PRICING CONSISTENCY AND CANCELLATION POLICIES:",
      data: Pricing,
    },
    {
      id: 4,
      title: "PROHIBITED CONDUCT, PENALTIES, AND USER DIVERSION:",
      data: Prohibited,
    },
    {
      id: 5,
      title: "RIGHTS & OBLIGATIONS OF THE PARTIES:",
      data: PartiesRights,
    },
    { id: 6, title: "WARRANTIES AND REPRESENTATIONS", data: Warranties },
    { id: 7, title: "INDEMNITY AND LIABILITY", data: Liability },
    { id: 8, title: "FORCE MAJEURE", data: ForceMajeure },
    { id: 9, title: "INDEMNITY", data: Indemnity },
    { id: 10, title: "INTELLECTUAL PROPERTY RIGHTS :", data: IPR },
    { id: 11, title: "CONFIDENTIALITY :", data: Confidentiality },
    { id: 12, title: "TERM AND TERMINATION :", data: TermCondition },
    { id: 13, title: "MISCELLANEOUS PROVISIONS :", data: Miscellaneous },
  ];
};

export default getClauses;
